import { useState } from 'react'

import { Box, Button, Spinner } from '@factorialco/deprecated-design-system'
import { F0Box, F0Button } from '@factorialco/f0-react'
import { Bell, Download, FileSigned } from '@factorialco/f0-react/icons/app'
import { useInterval } from 'usehooks-ts'

import { observer } from 'mobx-react'

import './EsignatureF0.styles.css'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { Document } from 'modules/documents/hooks/documents/useGetDocuments'
import {
  isBouncedSignatures,
  isCancelledSignatures,
  isDeclinedSignatures,
  isErrorSignatures,
  isExpiredSignatures,
  isInTrashBin,
  isPartiallySignedSignatures,
  isPendingSignatures,
  isPendingToFill,
  isPublicCompanyDocument,
} from 'modules/documents/lib/Documents'

import DocumentLogs from 'modules/documents/store/collections/logs'
import type DocumentLog from 'modules/documents/store/models/Log'
import { useGetEsignatureRequests } from 'modules/esignatures/hooks/requests/useGetEsignatureRequests'
import { useBulkResendRequestSignature } from 'modules/esignatures/hooks/signatures/useBulkResendRequestSignature'
import { useGetEsignatureSignatures } from 'modules/esignatures/hooks/signatures/useGetEsignatureSignatures'

import AuditTrailModal from './AuditTrailModal'
import MessageAlert from './MessageAlert'
import EsignatureModal from './Modal'
import SignaturesList from './SignaturesList'
import EsignatureTitle from './Title'
import { EsignatureF0Provider } from './useEsignatureF0Context'

interface Props {
  document: Document
  useF0?: boolean
}

const ESIGNATURES_REFRESH_INTERVAL = 5000

const Esignature = ({ document, useF0 = false }: Props) => {
  const [open, setOpen] = useState(false)
  const [openConfirmAuditTrail, setOpenConfirmAuditTrail] = useState(false)
  const [documentLogs] = useState(new DocumentLogs())

  const fetchLogs = (documentId: string) =>
    documentLogs.fetch({ data: { document_id: documentId, log_type: 'file_updated' } })

  const { loadingQL, signatures, startSignaturesPolling, stopSignaturesPolling } =
    useGetEsignatureSignatures(document.id)
  const { signatureRequests } = useGetEsignatureRequests(document.id)

  const resolvableSignatures = signatures.filter((s) => s.signee != null)

  const isSequential = signatureRequests.some((r) => r.signingOrder != null)
  const signingOrderBySigneeId = isSequential
    ? signatureRequests.reduce<Record<string, number>>((acc, r) => {
        if (r.signingOrder != null && r.signee != null) {
          acc[r.signee.id] = r.signingOrder + 1
        }
        return acc
      }, {})
    : undefined

  const requestSigneeIds = new Set(
    signatureRequests
      .filter((r) => r.signee != null)
      .map((r) => r.signee?.id)
      .filter(Boolean)
  )
  const visibleSignatures =
    requestSigneeIds.size > 0
      ? resolvableSignatures.filter((s) => s.signee && requestSigneeIds.has(s.signee.id))
      : resolvableSignatures

  const { bulkResendRequestSignature } = useBulkResendRequestSignature()

  const { loading } = useAsyncData(
    () => Promise.all([fetchLogs(document.id)]),
    [document.id, document.folder?.id]
  )

  const pendingSignatures = resolvableSignatures.filter((signature) => signature.pending)

  const {
    access: { id: accessId },
  } = useCurrentSession()
  const currentAccessRemoteUrl = resolvableSignatures.find(
    (signature) => signature.pending && signature.signee?.id === accessId
  )?.remoteUrl

  const hasPendingSignatures = !loadingQL && pendingSignatures.length > 0

  const showAuditTrailDownload = resolvableSignatures.length > 0

  const description = () => {
    if (isSequential && hasPendingSignatures) {
      return i18n.t('documents.modal.esignature.description_sequential')
    }

    if (hasPendingSignatures) {
      return i18n.t('documents.modal.esignature.description_pending')
    }

    if (signatures.length > 0) {
      return i18n.t('documents.modal.esignature.description_signed')
    }

    return i18n.t('documents.modal.esignature.description')
  }

  const indexedLogs = documentLogs.models.reduce(
    (indexed: { [id: string]: boolean }, log: DocumentLog) => {
      // eslint-disable-next-line no-type-assertion/no-type-assertion
      const id: string = log.attributes.payload['esignature_id'] as string
      indexed[id] = true
      return indexed
    },
    {}
  )

  const signedSignatures = signatures.filter((signature) => signature.signed)

  const completeUpdated = signedSignatures.every((signature) => indexedLogs[signature.id])

  const noSignatures = !signatures.length

  const waitingForSignatures = isPendingSignatures(document) && noSignatures

  const signatureRequestable =
    !isPendingSignatures(document) && noSignatures && !document.signatureStatus

  const showProcessingSignatureAlert = !completeUpdated

  const hideEsignature = isPublicCompanyDocument(document) && !hasPendingSignatures && noSignatures

  const showResendSignature =
    (isPendingSignatures(document) || isPartiallySignedSignatures(document)) &&
    !isPendingToFill(document)

  const showSignatureList = signatures.length > 0 || isErrorSignatures(document)
  const requestDate = signatureRequests[0]?.createdAt

  const resendSingnatureReminder = async () => {
    const result = await bulkResendRequestSignature({ documentIds: [document.id] })

    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.done'),
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_error'),
      })
    }
  }

  useInterval(() => {
    if (waitingForSignatures) startSignaturesPolling(ESIGNATURES_REFRESH_INTERVAL)
    else stopSignaturesPolling()

    if (!completeUpdated) return fetchLogs(document.id)
  }, ESIGNATURES_REFRESH_INTERVAL)

  const showTitle = signatures.length > 0 || !isInTrashBin(document)

  if (loading || loadingQL) {
    if (useF0) {
      return (
        <F0Box paddingY="lg" display="flex" flexDirection="column" gap="md" width="full">
          <F0Box width="32" height="5" background="secondary" borderRadius="sm" />
          <F0Box width="48" height="5" background="secondary" borderRadius="sm" />
        </F0Box>
      )
    }
    return (
      <Box alignItems="center">
        <Spinner />
      </Box>
    )
  }

  if (hideEsignature) return null

  const auditTrailDownloadButton = () =>
    useF0 ? (
      <F0Button
        variant="outline"
        icon={Download}
        label={i18n.t('documents.modal.esignature.download_audit_trail')}
        onClick={() => setOpenConfirmAuditTrail(true)}
      />
    ) : (
      <Button type="secondary" onClick={() => setOpenConfirmAuditTrail(true)} iconName="download">
        {i18n.t('documents.modal.esignature.download_audit_trail')}
      </Button>
    )

  return (
    <EsignatureF0Provider value={useF0}>
      <Box gap="s16">
        {open && (
          <EsignatureModal document={document} onClose={() => setOpen(false)} useF0={useF0} />
        )}
        {showTitle && <EsignatureTitle description={description()} />}
        {showSignatureList && document.signatureStatus && (
          <SignaturesList
            signatures={visibleSignatures}
            requestDate={requestDate}
            documentId={document.id}
            documentSignatureStatus={document.signatureStatus}
            signingOrderBySigneeId={signingOrderBySigneeId}
          />
        )}
        {isCancelledSignatures(document) && pendingSignatures.length > 0 && (
          <MessageAlert
            message={i18n.t('documents.modal.esignature.cancelling_alert')}
            alertColor="info"
            visibleLoader={false}
          />
        )}
        {isCancelledSignatures(document) && pendingSignatures.length === 0 && (
          <MessageAlert
            message={i18n.t('documents.modal.esignature.cancelled_alert')}
            helpUrl="esignature_request"
            helpText={i18n.t('documents.modal.esignature.learn_more')}
            alertColor="error"
            visibleLoader={false}
            id="canceledSignatureMessage"
          />
        )}

        {!isInTrashBin(document) && (
          <>
            {isPendingSignatures(document) && noSignatures && (
              <MessageAlert message={i18n.t('documents.modal.esignature.pending_alert')} />
            )}
            {showProcessingSignatureAlert && (
              <MessageAlert
                message={i18n.t('documents.modal.esignature.processing_signature_alert')}
              />
            )}
            {isBouncedSignatures(document) && (
              <MessageAlert
                message={i18n.t('documents.modal.esignature.bounced_email_alert')}
                helpUrl="esignature_request"
                helpText={i18n.t('documents.modal.esignature.learn_more')}
                alertColor="error"
                visibleLoader={false}
              />
            )}
            {isDeclinedSignatures(document) && (
              <MessageAlert
                message={i18n.t('documents.modal.esignature.declined_alert')}
                alertColor="warning"
                visibleLoader={false}
              />
            )}
            {isErrorSignatures(document) && (
              <MessageAlert
                message={i18n.t('documents.modal.esignature.signature_error_alert')}
                alertColor="error"
                visibleLoader={false}
              />
            )}

            {useF0 ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: 'var(--spacing-s8)',
                  flexWrap: 'wrap',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-s8)' }}>
                  {showAuditTrailDownload && auditTrailDownloadButton()}
                  {showResendSignature && (
                    <F0Button
                      variant="outline"
                      icon={Bell}
                      label={i18n.t('documents.modal.esignature.resend_signature')}
                      onClick={() => resendSingnatureReminder()}
                    />
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-s8)' }}>
                  {currentAccessRemoteUrl &&
                    !isErrorSignatures(document) &&
                    !isExpiredSignatures(document) && (
                      <F0Button
                        icon={FileSigned}
                        label={i18n.t('documents.modal.esignature.sign_button')}
                        onClick={() => window.location.assign(currentAccessRemoteUrl)}
                      />
                    )}
                  {signatureRequestable && (
                    <F0Button
                      label={i18n.t('documents.modal.esignature.request_esignature')}
                      onClick={() => setOpen(true)}
                    />
                  )}
                </div>
              </div>
            ) : (
              <Box alignItems="flexEnd" gap="s8">
                {currentAccessRemoteUrl &&
                  !isErrorSignatures(document) &&
                  !isExpiredSignatures(document) && (
                    <Button
                      onClick={() => window.location.assign(currentAccessRemoteUrl)}
                      iconName="sign"
                    >
                      {i18n.t('documents.modal.esignature.sign_button')}
                    </Button>
                  )}
                {signatureRequestable && (
                  <Button onClick={() => setOpen(true)}>
                    {i18n.t('documents.modal.esignature.request_esignature')}
                  </Button>
                )}
                {showAuditTrailDownload && auditTrailDownloadButton()}
                {showResendSignature && (
                  <Button
                    type="secondary"
                    onClick={() => resendSingnatureReminder()}
                    iconName="bell"
                  >
                    {i18n.t('documents.modal.esignature.resend_signature')}
                  </Button>
                )}
              </Box>
            )}
          </>
        )}

        {isInTrashBin(document) && showAuditTrailDownload && (
          <Box alignItems="flexEnd" gap="s8">
            {auditTrailDownloadButton()}
          </Box>
        )}

        {openConfirmAuditTrail && signatures[0] && (
          <AuditTrailModal
            signature={signatures[0]}
            onClose={() => setOpenConfirmAuditTrail(false)}
            useF0={useF0}
          />
        )}
      </Box>
    </EsignatureF0Provider>
  )
}

export default observer(Esignature)
